import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";
import { FIELD_LABELS as FORM_FIELD_LABELS } from "../../data/serviceForms";
import { ArrowLeft, FileText, Trash2, ExternalLink, Plus, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { SERVICES } from "../../data/staticData";

const STATUS_OPTIONS = ["pending", "in_review", "in_progress", "delivered", "cancelled"];

const TYPE_OPTIONS = [
  { v: "pdf", l: "PDF" },
  { v: "gdrive", l: "Google Drive" },
  { v: "csv", l: "CSV" },
  { v: "link", l: "Link" },
];

function labelize(key) {
  if (FORM_FIELD_LABELS[key]) return FORM_FIELD_LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("pending");
  const [adminNotes, setAdminNotes] = useState("");
  const [newDeliv, setNewDeliv] = useState({ label: "", url: "", type: "gdrive", note: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadNote, setUploadNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/orders/${id}`);
    setOrder(data);
    setStatus(data.status);
    setAdminNotes(data.admin_notes || "");
  };

  useEffect(() => { load(); }, [id]);

  if (!order) return <AdminLayout><div className="text-sm text-inkSoft">Loading…</div></AdminLayout>;

  const service = SERVICES.find((s) => s.slug === order.service_slug);
  const reqEntries = Object.entries(order.requirements || {});

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      await api.patch(`/admin/orders/${id}`, { status, admin_notes: adminNotes });
      toast.success("Order updated. Customer will see the new status.");
      await load();
    } catch (e) {
      toast.error("Could not update.");
    } finally {
      setSavingStatus(false);
    }
  };

  const addDeliverable = async (e) => {
    e.preventDefault();
    if (!newDeliv.label || !newDeliv.url) {
      toast.error("Label and URL are required.");
      return;
    }
    try {
      await api.post(`/admin/orders/${id}/deliverables`, newDeliv);
      toast.success("Deliverable added.");
      setNewDeliv({ label: "", url: "", type: "gdrive", note: "" });
      await load();
    } catch (e) {
      toast.error("Could not add deliverable.");
    }
  };

  const removeDeliverable = async (idx) => {
    if (!window.confirm("Remove this deliverable?")) return;
    try {
      await api.delete(`/admin/orders/${id}/deliverables/${idx}`);
      toast.success("Removed.");
      await load();
    } catch (e) {
      toast.error("Could not remove.");
    }
  };

  const uploadReport = async (e) => {
    e.preventDefault();
    if (!uploadFile) return toast.error("Choose a file first");
    if (!uploadLabel) return toast.error("Give it a label");
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("label", uploadLabel);
    if (uploadNote) fd.append("note", uploadNote);
    setUploading(true);
    try {
      await api.post(`/admin/orders/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded. Customer notified by email.");
      setUploadFile(null);
      setUploadLabel("");
      setUploadNote("");
      const inp = document.getElementById(`upload-input-${id}`);
      if (inp) inp.value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout>
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-sm text-inkSoft hover:text-ink mb-4"><ArrowLeft size={14} /> Back to orders</Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Order · <span className="font-mono">{order.order_id}</span></div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="admin-order-title">{service?.title || order.service_slug}</h1>
          <p className="text-inkSoft mt-1">{order.package_name} · <span className="font-mono">${order.price ?? "—"}</span></p>
        </div>
        <div className="text-right text-sm">
          <div className="font-medium">{order.customer_name}</div>
          <div className="text-inkSoft text-xs">{order.customer_email}</div>
        </div>
      </div>

      {/* Payment info */}
      {(order.payment_status || order.stripe_session_id) && (
        <div className="mt-6 card-soft p-6" data-testid="admin-payment-info">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Payment</div>
              <h2 className="font-display text-xl font-bold tracking-tight mt-1">Invoice & receipt</h2>
            </div>
            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${order.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
              {order.payment_status || "—"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-inkSoft font-mono">Amount paid</div>
              <div className="mt-1 font-mono">{order.amount_paid ? `$${(order.amount_paid / 100).toFixed(2)}` : "—"} <span className="text-inkSoft uppercase text-[10px]">{order.currency || ""}</span></div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-inkSoft font-mono">Paid at</div>
              <div className="mt-1">{order.paid_at ? new Date(order.paid_at).toLocaleString() : "—"}</div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[11px] uppercase tracking-widest text-inkSoft font-mono">Stripe session</div>
              <div className="mt-1 font-mono text-xs break-all">{order.stripe_session_id || "—"}</div>
            </div>
            {order.receipt_url && (
              <div className="sm:col-span-2">
                <a href={order.receipt_url} target="_blank" rel="noreferrer" className="btn-secondary text-xs inline-flex" data-testid="admin-view-receipt">
                  <ExternalLink size={12} /> View Stripe receipt / invoice
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status + admin notes */}
      <div className="mt-6 grid md:grid-cols-3 gap-5">
        <div className="card-soft p-6">
          <div className="eyebrow mb-3">Update status</div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
            data-testid="admin-status-select"
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
          <p className="mt-3 text-xs text-inkSoft">Progress bar updates automatically based on status.</p>
        </div>
        <div className="card-soft p-6 md:col-span-2">
          <div className="eyebrow mb-3">Admin notes (internal only)</div>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={3}
            placeholder="Notes for the fulfillment team — never shown to the customer"
            className="w-full rounded-xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink"
            data-testid="admin-notes-textarea"
          />
          <div className="mt-3 text-right">
            <button onClick={saveStatus} disabled={savingStatus} className="btn-primary text-sm" data-testid="admin-save-status">
              <Save size={14} /> {savingStatus ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Customer brief */}
      <div className="mt-6 card-soft p-6" data-testid="admin-brief">
        <div className="eyebrow mb-3">Customer brief</div>
        {reqEntries.length === 0 ? (
          <div className="text-sm text-inkSoft">No brief provided.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {reqEntries.map(([k, v]) => {
              const isUrl = typeof v === "string" && /^https?:\/\//.test(v);
              return (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-widest text-inkSoft font-mono">{labelize(k)}</div>
                  <div className="mt-1 text-sm break-words">
                    {isUrl ? (
                      <a href={v} target="_blank" rel="noreferrer" className="text-terra hover:underline inline-flex items-center gap-1">
                        {v} <ExternalLink size={12} />
                      </a>
                    ) : (
                      <span>{String(v)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Deliverables */}
      <div className="mt-6 card-soft p-6" data-testid="admin-deliverables">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Deliverables</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Reports & links</h2>
          </div>
          <span className="text-xs text-inkSoft">Add a Google Drive link, PDF URL or CSV — the customer sees it immediately.</span>
        </div>

        {(order.deliverables || []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-line p-6 text-center text-sm text-inkSoft">No deliverables yet.</div>
        ) : (
          <ul className="space-y-2 mb-5">
            {order.deliverables.map((d, i) => (
              <li key={i} className="flex items-center justify-between p-3 rounded-xl border border-line bg-bg">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-surfaceAlt grid place-items-center text-terra shrink-0"><FileText size={14} /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.label}</div>
                    <a href={d.url} target="_blank" rel="noreferrer" className="text-xs text-inkSoft hover:text-terra truncate inline-flex items-center gap-1">
                      <span className="truncate">{d.url}</span> <ExternalLink size={10} />
                    </a>
                    {d.note && <div className="text-[11px] text-inkSoft mt-1">{d.note}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="tag font-mono text-[10px] uppercase">{d.type}</span>
                  <button onClick={() => removeDeliverable(i)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-red-50 text-red-600" data-testid={`deliv-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* File upload block (PDF, CSV, DOCX etc.) */}
        <form onSubmit={uploadReport} className="rounded-2xl border border-dashed border-line bg-white p-5 mb-4" data-testid="admin-upload-form">
          <div className="flex items-center gap-2 mb-3">
            <Upload size={14} className="text-terra" />
            <div className="font-semibold text-sm">Upload a file (PDF, CSV, DOCX, image — up to 25 MB)</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
            <input
              id={`upload-input-${id}`}
              type="file"
              accept=".pdf,.csv,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg,.webp,.zip"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="sm:col-span-5 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-ink file:text-white file:cursor-pointer"
              data-testid="upload-file-input"
            />
            <input
              value={uploadLabel}
              onChange={(e) => setUploadLabel(e.target.value)}
              placeholder="Label (e.g. Final report)"
              className="sm:col-span-3 h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
              data-testid="upload-label"
            />
            <input
              value={uploadNote}
              onChange={(e) => setUploadNote(e.target.value)}
              placeholder="Note (optional)"
              className="sm:col-span-2 h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
              data-testid="upload-note"
            />
            <button type="submit" disabled={uploading} className="sm:col-span-2 btn-primary text-sm justify-center" data-testid="upload-submit">
              {uploading ? "Uploading…" : (<><Upload size={14} /> Upload</>)}
            </button>
          </div>
          <p className="text-[11px] text-inkSoft mt-3">Uploaded files are stored securely on our server. Only the assigned customer and admins can download them.</p>
        </form>

        <div className="text-xs text-inkSoft mb-2 flex items-center gap-1">
          <span>Or paste an external URL (Google Drive, Dropbox, hosted PDF, etc.):</span>
        </div>
        <form onSubmit={addDeliverable} className="grid grid-cols-1 sm:grid-cols-12 gap-3 bg-surfaceAlt/50 rounded-2xl p-4" data-testid="admin-add-deliverable">
          <input
            value={newDeliv.label}
            onChange={(e) => setNewDeliv({ ...newDeliv, label: e.target.value })}
            placeholder="Label (e.g. Final report)"
            className="sm:col-span-3 h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
            data-testid="deliv-label"
          />
          <input
            type="url"
            value={newDeliv.url}
            onChange={(e) => setNewDeliv({ ...newDeliv, url: e.target.value })}
            placeholder="https://drive.google.com/… or https://example.com/report.pdf"
            className="sm:col-span-5 h-11 px-4 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
            data-testid="deliv-url"
          />
          <select
            value={newDeliv.type}
            onChange={(e) => setNewDeliv({ ...newDeliv, type: e.target.value })}
            className="sm:col-span-2 h-11 px-3 rounded-xl border border-line bg-white text-sm outline-none focus:border-ink"
            data-testid="deliv-type"
          >
            {TYPE_OPTIONS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <button type="submit" className="sm:col-span-2 btn-primary text-sm justify-center" data-testid="deliv-add">
            <Plus size={14} /> Add
          </button>
        </form>
      </div>
    </AdminLayout>
  );
}
